import React, {
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from 'react';
import { useParams } from 'react-router-dom';
import { useHistory } from 'react-router';
import { Button } from '@material-ui/core';
import { toast } from 'react-toastify';
import { instance } from '../../services/api';
import TableGenerator from '../../generators/TableGenerator';
import { GlobalsContext } from '../../store/Globals';
import CurlPanel from '../../components/CurlPanel';

import { getISODateTimeString } from '../../utils/dateUtils';
import ListTypeModal from './ListTypeModal';
import TokenModal from './TokenModal';
import DateTimeModal from './DateTimeModal';

import { Container } from './styles';
import ObjectModal from './ObjectModal';

type Field = {
  tag: string;
  dataType: string;
  dropDownValues: { [index: string]: number } | null;
};

const ListScreen: React.FC<{
  asset: {
    label: string;
    tag: string;
    readers: string[];
    writers: string[];
    canCreate: boolean;
    dynamic: boolean;
    collection?: string;
  };
}> = ({ asset }) => {
  const { key } = useParams<{ key: string }>();
  const globals = useContext(GlobalsContext);
  const [pageLimit, setPageLimit] = useState(10);
  const history = useHistory();
  const [columns, setColumns] = useState<{ title: string; field: string }[]>(
    [],
  );
  const [data, setData] = useState<any[]>([]);
  const [listModalData, setListModalData] = useState({
    listData: [''],
    propType: '',
    tag: '',
    open: false,
  });
  const [openIDToken, setOpenIDToken] = useState<string | null>(null);
  const [object, setObject] = useState<string | null>(null);
  const [dateTime, setDateTime] = useState<string | null>(null);
  const [canWrite, setCanWrite] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState(false);
  const [curl, setCURL] = useState<{
    method: string;
    url: string;
    jsonObj: any;
    variable: string;
  }>({ method: '', url: '', jsonObj: {}, variable: '' });
	const [readers, setReaders] = useState<string[] | null>(null);
	const [searchTerm, setSearchTerm] = useState('');
	const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
	const [contractIdSearch, setContractIdSearch] = useState('');
	const [debouncedContractId, setDebouncedContractId] = useState('');
	const [currentBookmark, setCurrentBookmark] = useState('');
	const [bookmarkHistory, setBookmarkHistory] = useState<string[]>([]);
	const [hasNextPage, setHasNextPage] = useState(false);
	const [schemaLoaded, setSchemaLoaded] = useState(false);
	const fieldsRef = useRef<Field[]>([]);

  const renderButton = useCallback(
    (itemAsset: any) => {
      if (
        typeof itemAsset === 'string' ||
        (typeof itemAsset === 'object' && itemAsset['@hash'])
      ) {
        return (
          <div>
            <Button disabled variant="outlined">
              {`Private: ${typeof itemAsset === 'object' && itemAsset['@hash']
                  ? itemAsset['@hash']
                  : itemAsset
                }`}
            </Button>
          </div>
        );
      }

      let firstTag: keyof Record<string, any> = Object.keys(itemAsset).find(
        (tag) => tag.charAt(0) !== '@' && typeof itemAsset[tag] === 'string',
      ) as keyof Record<string, any>;

      if (!firstTag) {
        firstTag = Object.keys(itemAsset).find(
          (tag) => tag.charAt(0) !== '@',
        ) as keyof Record<string, any>;
      }

      const keyId = itemAsset['@key'];
      return firstTag ? (
        <div>
          <Button
            onClick={() => {
              if (keyId) {
                history.push(`/show/${keyId}`);
              }
            }}
            disabled={!keyId}
            variant="outlined"
          >
            {globals.itemLabel(itemAsset, true)}
          </Button>
        </div>
      ) : keyId ? (
        <div>
          <Button
            onClick={() => {
              if (keyId) {
                history.push(`/show/${keyId}`);
              }
            }}
            disabled={!keyId}
            variant="outlined"
          >
            {keyId}
          </Button>
        </div>
      ) : null;
    },
    [globals, history],
  );

  const setInitialData = useCallback(
    async (result: any, reset: boolean) => {
      const newData = result
        ? result.map((item: any) => {
          const newItem = item;

          Object.keys(newItem).forEach((objKey) => {
            if (typeof newItem[objKey] === 'boolean')
              newItem[objKey] = newItem[objKey] ? 'YES' : 'NO';

            if (typeof newItem[objKey] === 'string') {
              if (objKey === 'openIDToken') {
                newItem[objKey] = (
                  <Button
                    disabled={false}
                    variant="outlined"
                    onClick={(e: any) => {
                      if (e.currentTarget)
                        setOpenIDToken(e.currentTarget.value);
                    }}
                    value={item[objKey].slice(0)}
                  >
                    OpenId Token
                  </Button>
                );
              } else {
                newItem[objKey] =
                  newItem[objKey].substring(0, 70) +
                  (newItem[objKey].length > 70 ? '...' : '');
              }
            }
          });

          if (key) {
            Object.keys(newItem).forEach((objKey) => {
              if (objKey === '_timestamp') {
                newItem[objKey] = getISODateTimeString(newItem[objKey]);
              } else if (objKey === '@lastTouchBy') {
                newItem[objKey] =
                  newItem[objKey].split('MSP').length > 0
                    ? newItem[objKey].split('MSP')[0]
                    : newItem[objKey];
              }
            });
          }

          fieldsRef.current.forEach((field) => {
            if (field.dataType === 'datetime') {
              newItem[field.tag] = item[field.tag] ? (
                <div>
                  <Button
                    variant="outlined"
                    disabled={false}
                    value={item[field.tag]}
                    onClick={(e: any) => {
                      if (e.currentTarget) setDateTime(e.currentTarget.value);
                    }}
                                       >
                       <i className="bx bx-time" style={{ marginRight: '4px' }}></i>
                       {getISODateTimeString(item[field.tag])}
                     </Button>
                </div>
              ) : null;

              return;
            }

            if (field.dataType === '@object' || field.dataType === 'object') {
              newItem[field.tag] = item[field.tag] ? (
                <div>
                  <Button
                    disabled={false}
                    variant="outlined"
                    onClick={(e: any) => {
                      if (e.currentTarget) setObject(e.currentTarget.value);
                    }}
                    value={JSON.stringify(item[field.tag])}
                  >
                    <i className="bx bx-show" style={{ marginRight: '4px' }}></i>
                    @Object
                  </Button>
                </div>
              ) : null;

              return;
            }

            if (field.dropDownValues) {
              const rawValue = item[field.tag];

              // Handle array dropdown values (e.g. []permission) — render as tags
              if (Array.isArray(rawValue)) {
                const labels = rawValue.map((val: any) => {
                  const entry = Object.entries(field.dropDownValues!).find(
                    ([k, v]) => k === val || v === val,
                  );
                  return entry ? String(entry[1]) : String(val);
                });
                newItem[field.tag] = (
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '3px',
                    maxHeight: '52px',
                    overflow: 'hidden',
                    whiteSpace: 'normal' as any,
                  }}>
                    {labels.map((lbl: string, idx: number) => (
                      <span
                        key={idx}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '3px',
                          padding: '1px 8px',
                          border: '1px solid #e0e0e0',
                          borderRadius: '10px',
                          background: '#f5f5f5',
                          fontSize: '11px',
                          color: '#555',
                          whiteSpace: 'nowrap',
                          lineHeight: '20px',
                        }}
                      >
                        <i className="bx bxs-circle" style={{ fontSize: '4px', color: '#66bb6a' }} />
                        {lbl}
                      </span>
                    ))}
                  </div>
                );
                return;
              }

              const entrie = Object.entries(field.dropDownValues).find(
                ([k, v]) => k === rawValue || v === rawValue,
              );

              if (entrie) newItem[field.tag] = String(entrie[1]) || entrie[0];

              return;
            }

            if (field.dataType.indexOf('[]') >= 0) {
              const listData = newItem[field.tag];
              newItem[field.tag] = listData ? (
                <div>
                  <Button
                    variant="outlined"
                    disabled={false}
                    onClick={() => {
                      setListModalData({
                        open: true,
                        propType: field.dataType.replace('[]', ''),
                        listData,
                        tag: asset.tag,
                      });
                    }}
                  >
                    <i className="bx bx-list-ul" style={{ marginRight: '4px' }}></i>
                    [{listData.length}]
                  </Button>
                </div>
              ) : null;

              return;
            }

            if (field.dataType.indexOf('->') >= 0) {
              const itemAsset = item[field.tag];

              if (itemAsset && itemAsset['@key']) {
                newItem[field.tag] = renderButton(itemAsset);
              }
            }
          });

          return newItem;
        })
        : null;

      if (key) {
        setData(newData);
      } else {
        setData((state) => [...(!reset ? state : []), ...newData]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [asset.tag, key, renderButton],
  );

	const isValidUUID = (uuid: string): boolean => {
		const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
		return uuidRegex.test(uuid);
	};

	const loadByContractId = useCallback(
		async (contractId: string) => {
			if (!contractId || !isValidUUID(contractId)) {
				if (contractId && contractId.length > 0) {
					toast.warning('Invalid Contract ID format.');
				}
				return;
			}

			setIsLoading(true);

			try {
				const { data } = await instance.get(`/api/debt/${contractId}`);

				setCURL({
					method: 'GET',
					url: `/api/debt/${contractId}`,
					jsonObj: {},
					variable: '',
				});

				setInitialData([data], true);
				setHasNextPage(false);
				setCurrentBookmark('');
			} catch (err: any) {
				if (err?.response?.status === 404) {
					toast.error('Debt not found with this Contract ID');
					setInitialData([], true);
				} else {
					toast.error('Error searching by Contract ID');
				}
			}

			setIsLoading(false);
		},
		[setInitialData],
	);

	const loadData = useCallback(
		async (bookmark: string = '', term: string = '') => {
			setIsLoading(true);

			if (!key) {
        const selector: any = { '@assetType': asset.tag };
        
        if (term && fieldsRef.current.length > 0) {
          const primaryFields = ['nome', 'name'];
          const secondaryFields = ['documento', 'descricao', 'description'];
          
          const allSearchableFields = fieldsRef.current.filter(f => {
            const dt = f.dataType.toLowerCase();
            return dt === 'string' && 
                   !dt.includes('datetime') && 
                   !dt.includes('->') && 
                   !dt.includes('[]') &&
                   !f.tag.startsWith('@');
          });
          
          const primary = allSearchableFields.filter(f => 
            primaryFields.some(pf => f.tag.toLowerCase() === pf)
          );
          
          const secondary = allSearchableFields.filter(f => 
            secondaryFields.some(sf => f.tag.toLowerCase() === sf)
          );
          
          const searchableFields = [...primary, ...secondary].slice(0, 3);
          
          if (searchableFields.length > 0) {
            const words = term
              .trim()
              .split(/\s+/)
              .filter(w => w.length >= 3)
              .slice(0, 2)
              .map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
            
            if (words.length > 0) {
              selector.$or = searchableFields.flatMap(field => {
                if (words.length === 1) {
                  return [{ [field.tag]: { $regex: words[0] } }];
                }
                
                return words.map(word => ({
                  [field.tag]: { $regex: word }
                }));
              });
            }
          }
        }

        const queryReq = {
          query: {
            selector,
            ...(pageLimit !== 0 && !readers && { limit: pageLimit }),
            ...(pageLimit !== 0 && !readers && { bookmark }),
          },
          ...(readers && {
            collection: asset.collection ? asset.collection : asset.tag,
          }),
          resolve: false,
        };

        try {
          const {
            data: { result, metadata },
          } = (await instance.post(`/api/query/search`, queryReq)) as any;

          setHasNextPage(!!metadata?.bookmark && metadata.bookmark !== '');
          
          if (metadata?.bookmark) {
            setCurrentBookmark(metadata.bookmark);
          }

          setCURL({
            method: 'POST',
            url: `/api/query/search`,
            jsonObj: queryReq,
            variable: ``,
          });

          setInitialData(result, true);
        } catch (err: any) {
          if (
            err?.response?.status === 500 &&
            typeof err.response.data === 'string' &&
            err.response.data.indexOf('does not have read access') >= 0
          ) {
            toast.error('No reading permission');
          } else {
            toast.error('Error loading data');
          }
        }
      } else {
        const queryReq = {
          key: { '@assetType': asset.tag, '@key': key, resolve: true },
          resolve: true,
        };

        try {
          const result = await instance.post(
            `/api/query/readAssetHistory`,
            queryReq,
          );

          setCURL({
            method: 'POST',
            url: '/api/query/readAssetHistory',
            jsonObj: queryReq,
            variable: ``,
          });

          setInitialData(result.data, true);
        } catch (err: any) {
          toast.error('Error loading history');
        }
      }
      setIsLoading(false);
    },
    [asset.tag, asset.collection, key, setInitialData, pageLimit, readers],
  );

	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearchTerm(searchTerm);
		}, 500);

		return () => clearTimeout(timer);
	}, [searchTerm]);

	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedContractId(contractIdSearch);
		}, 500);

		return () => clearTimeout(timer);
	}, [contractIdSearch]);

	useEffect(() => {
		if (asset.tag === 'debt' && debouncedContractId && !key) {
			setBookmarkHistory([]);
			setCurrentBookmark('');
			loadByContractId(debouncedContractId);
		} else if (schemaLoaded && !key && !debouncedContractId) {
			setBookmarkHistory([]);
			setCurrentBookmark('');
			loadData('', debouncedSearchTerm);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debouncedContractId, debouncedSearchTerm, schemaLoaded, pageLimit]);

	useEffect(() => {
		setSchemaLoaded(false);
    
    const getSchema = async () => {
      const result = await instance.post('/api/query/getSchema/', {
        assetType: asset.tag,
      });
      const resultData = result.data;
      const columnValues: { title: string; field: string }[] = [];

      if (key) {
        columnValues.push({ title: 'Organization', field: '@lastTouchBy' });
        columnValues.push({ title: 'Time Stamp', field: '_timestamp' });
      }

      fieldsRef.current = [];
      resultData.props.forEach(
        (item: { label: string; tag: string; dataType: string }) => {
          columnValues.push({ title: item.label, field: item.tag });
          const dropDownValues = globals.getDropDownValues(item.dataType);

          fieldsRef.current.push({
            tag: item.tag,
            dataType: item.dataType,
            dropDownValues: dropDownValues || null,
          } as Field);
        },
      );

      setColumns(columnValues);

      if (resultData && resultData.readers) {
        return setReaders(resultData.readers);
      }
      return setReaders(null);
    };

    getSchema().then(() => {
      setCanWrite(globals.checkPermission(asset.writers));
      setSchemaLoaded(true);
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset.tag, history, key, asset.writers, globals]);

  const handleNextPage = useCallback(() => {
    if (currentBookmark && hasNextPage) {
      setBookmarkHistory(prev => [...prev, currentBookmark]);
      loadData(currentBookmark, debouncedSearchTerm);
    }
  }, [currentBookmark, hasNextPage, loadData, debouncedSearchTerm]);

  const handlePreviousPage = useCallback(() => {
    if (bookmarkHistory.length > 0) {
      const newHistory = [...bookmarkHistory];
      newHistory.pop();
      setBookmarkHistory(newHistory);
      
      const bookmarkToUse = newHistory[newHistory.length - 1] || '';
      loadData(bookmarkToUse, debouncedSearchTerm);
    } else {
      setCurrentBookmark('');
      setBookmarkHistory([]);
      loadData('', debouncedSearchTerm);
    }
  }, [bookmarkHistory, loadData, debouncedSearchTerm]);

	const handleSearch = useCallback((term: string) => {
		setSearchTerm(term);
		setContractIdSearch('');
		setCurrentBookmark('');
		setBookmarkHistory([]);
	}, []);

	const handleContractIdSearch = useCallback((contractId: string) => {
		setContractIdSearch(contractId);
		setSearchTerm('');
		setCurrentBookmark('');
		setBookmarkHistory([]);
	}, []);

	const handleRemoveItem = useCallback((itemKey: string) => {
		setData((state) => state.filter((item) => item['@key'] !== itemKey));
	}, []);

	const handlePageLimit = useCallback((limit: string) => {
		const newLimit = parseInt(limit);
		setPageLimit(newLimit);
		setCurrentBookmark('');
		setBookmarkHistory([]);
	}, []);

  return (
    <div
      style={{
        paddingTop: 30,
      }}
    >
      <ListTypeModal
        {...listModalData}
        setOpen={(value: boolean) => {
          setListModalData((prev) => ({ ...prev, open: value }));
        }}
      />
      <Container>
        <CurlPanel
          method={curl.method}
          url={curl.url}
          obj={curl.jsonObj}
          variable={curl.variable}
        />
      </Container>
      <TokenModal
        openIDToken={openIDToken}
        setOpenIDToken={(value: string | null) => setOpenIDToken(value)}
      />
      <DateTimeModal
        dateTime={dateTime}
        setDateTime={(value: string | null) => setDateTime(value)}
      />
      <ObjectModal
        object={object}
        setObject={(value: string | null) => setObject(value)}
      />
		<TableGenerator
			ky={key}
			data={data}
			columns={columns}
			isLoading={isLoading}
			setIsLoading={(value: boolean) => setIsLoading(value)}
			asset={asset}
			canWrite={canWrite}
			updateData={() => loadData('', debouncedSearchTerm)}
			handleRemoveItem={handleRemoveItem}
			handlePageLimit={handlePageLimit}
			pageLimit={pageLimit}
			searchTerm={searchTerm}
			onSearchChange={handleSearch}
			contractIdSearch={contractIdSearch}
			onContractIdChange={handleContractIdSearch}
			onNextPage={handleNextPage}
			onPreviousPage={handlePreviousPage}
			hasNextPage={hasNextPage}
			hasPreviousPage={bookmarkHistory.length > 0}
		/>
    </div>
  );
};

export default ListScreen;
